// ══════════════════════════════════════════════════════════════
//  ScanPanel — Isometric Exploded-Stack Building Visualisation
//  Based on orto.space design handoff (iso-engine/iso-scene).
//  Reads ExtrudedPolygonVolumes, projects isometrically,
//  renders with floor-based colours, volume-style toggle,
//  explode slider, floor rail, hover labels, room detail popover.
// ══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// ── Types ───────────────────────────────────────────────────

interface RoomShape {
  id: number;
  label: string;
  name: string;
  floorId: string | null;
  worldPts: { x: number; y: number; z: number }[];
  height: number;
  usage: string;
  area: number;
  status: string;
}

interface ScanPanelProps {
  selectedVolumeIds: Set<number>;
  hoverVolumeId: number | null;
  onToggleVolume: (id: number) => void;
  onDeselectAll: () => void;
  onHoverVolume: (id: number | null) => void;
  compact?: boolean;
}

// ── Volume style type ──────────────────────────────────────

type VolumeStyle = 'solid' | 'wireframe' | 'glass' | 'footprint';

// ── Floor ordering (bottom → top) ──────────────────────────

const FLOOR_ORDER: Record<string, number> = {
  UG2: 0, UG1: 1, EG: 2, OG1: 3, OG2: 4, OG3: 5, DG: 6,
};

// ── Floor colour palette (from design: PALETTE.floors — 6 entries) ──

const FLOOR_PALETTE: {
  stroke: string; top: string; right: string; front: string;
}[] = [
  { stroke: '#3b82f6', top: 'rgba(147, 197, 253, 0.70)', right: 'rgba(96, 165, 250, 0.55)', front: 'rgba(59, 130, 246, 0.40)' },
  { stroke: '#10b981', top: 'rgba(110, 231, 183, 0.70)', right: 'rgba(52, 211, 153, 0.55)', front: 'rgba(16, 185, 129, 0.40)' },
  { stroke: '#f59e0b', top: 'rgba(253, 230, 138, 0.75)', right: 'rgba(251, 191, 36, 0.55)', front: 'rgba(245, 158, 11, 0.40)' },
  { stroke: '#ec4899', top: 'rgba(251, 207, 232, 0.75)', right: 'rgba(244, 114, 182, 0.55)', front: 'rgba(236, 72, 153, 0.40)' },
  { stroke: '#8b5cf6', top: 'rgba(196, 181, 253, 0.70)', right: 'rgba(167, 139, 250, 0.55)', front: 'rgba(139, 92, 246, 0.40)' },
  { stroke: '#06b6d4', top: 'rgba(165, 243, 252, 0.70)', right: 'rgba(103, 232, 249, 0.55)', front: 'rgba(34, 211, 238, 0.40)' },
];

// Selection palette (from design: PALETTE.flat.sel*)
const SEL = {
  stroke: '#2563eb',
  top: 'rgba(96, 165, 250, 0.7)',
  right: 'rgba(59, 130, 246, 0.55)',
  front: 'rgba(37, 99, 235, 0.45)',
};

// Ghost colours
const GHOST = {
  top: 'rgba(200,200,210,0.15)',
  right: 'rgba(170,170,180,0.10)',
  front: 'rgba(140,140,150,0.08)',
  stroke: 'rgba(100,100,110,0.35)',
};

// ── Alpha multiplier for glass mode ────────────────────────

function mulAlpha(color: string, mul: number): string {
  // Handle rgba/rgb
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const parts = m[1].split(',').map(s => parseFloat(s.trim()));
    const [r, g, b, a = 1] = parts;
    return `rgba(${r}, ${g}, ${b}, ${(a * mul).toFixed(3)})`;
  }
  // Handle hex (#rgb, #rrggbb)
  const hm = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hm) {
    let hex = hm[1];
    if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${mul.toFixed(3)})`;
  }
  return color;
}

// ── Isometric Projection ───────────────────────────────────

const COS30 = Math.cos(Math.PI / 6);
const SIN30 = 0.5;

function toIso(x: number, y: number, z: number) {
  // (y - x) mirrors horizontally vs classic (x - y), matching Potree's
  // coordinate convention where +Y = north and the 3D viewer looks
  // from the SE. Without the flip the iso view appears mirrored.
  return {
    x: (y - x) * COS30,
    y: (x + y) * SIN30 - z,
  };
}

function pts2path(pts: { x: number; y: number }[]): string {
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join('') + 'Z';
}

// ── Shoelace area ───────────────────────────────────────────

function shoelaceArea(pts: { x: number; y: number }[], sx: number, sy: number): number {
  let a = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const cur = pts[i];
    const next = pts[(i + 1) % n];
    a += cur.x * next.y - next.x * cur.y;
  }
  return Math.abs(a / 2) * sx * sy;
}

// ── Polygon winding (signed area via shoelace) ────────────

/** Returns true if polygon is CW in world XY (viewed from +Z). */
function isClockwise(pts: { x: number; y: number }[]): boolean {
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    sum += (b.x - a.x) * (b.y + a.y);
  }
  return sum > 0;
}

// ── Backface culling ───────────────────────────────────────

function isFrontFace(
  topA: { x: number; y: number },
  topB: { x: number; y: number },
  cw: boolean,
): boolean {
  // With projection sx=(y-x)*cos30, camera at (+x,+y):
  // a side wall is visible iff its outward normal faces toward camera.
  // For CW polygons (viewed from +Z), dsx > 0 = front-facing.
  // For CCW polygons the test is inverted.
  const dsx = topB.x - topA.x;
  return cw ? dsx > 0 : dsx < 0;
}

// ── Read room volumes from Potree ──────────────────────────

function readRoomShapes(): RoomShape[] {
  const vols: any[] = (window as any)._clipExtrudedVolumes || [];
  return vols
    .filter(v => v._volumeType === 'room' && !v._isClipProxy)
    .map(vol => {
      const profile: { x: number; y: number }[] = vol.profile || [];
      const pos = vol.position || { x: 0, y: 0, z: 0 };
      const sc = vol.scale || { x: 1, y: 1, z: 1 };
      const sx = Math.abs(sc.x);
      const sy = Math.abs(sc.y);
      const rb = vol._raumbuch || {};

      return {
        id: vol.id,
        label: rb.nr || vol.label || `R${vol.id}`,
        name: vol.name || '',
        floorId: vol._floorId,
        worldPts: profile.map(p => ({
          x: pos.x + p.x * sx,
          y: pos.y + p.y * sy,
          z: pos.z,
        })),
        height: Math.abs(sc.z),
        usage: vol._usage || '',
        area: shoelaceArea(profile, sx, sy),
        status: rb.status || 'Offen',
      };
    });
}

// ── Volume Style Icons (inline SVG — from design) ──────────

function VolumeIcon({ kind, active }: { kind: VolumeStyle; active: boolean }) {
  const s = active ? '#fff' : '#374151';
  const f = active ? '#4b5563' : '#d1d5db';

  if (kind === 'solid') return (
    <svg width="12" height="12" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
      <polygon points="3,6 8,3 13,6 13,12 8,15 3,12" fill={f} stroke={s} strokeWidth="1"/>
      <polygon points="3,6 8,9 13,6" fill={f} stroke={s} strokeWidth="1"/>
      <line x1="8" y1="9" x2="8" y2="15" stroke={s} strokeWidth="1"/>
    </svg>
  );
  if (kind === 'wireframe') return (
    <svg width="12" height="12" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
      <polygon points="3,6 8,3 13,6 13,12 8,15 3,12" fill="none" stroke={s} strokeWidth="1"/>
      <polyline points="3,6 8,9 13,6" fill="none" stroke={s} strokeWidth="1"/>
      <line x1="8" y1="9" x2="8" y2="15" stroke={s} strokeWidth="1"/>
    </svg>
  );
  if (kind === 'glass') return (
    <svg width="12" height="12" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
      <polygon points="3,6 8,3 13,6 13,12 8,15 3,12" fill={f} fillOpacity={0.3} stroke={s} strokeWidth="1"/>
      <polyline points="3,6 8,9 13,6" fill="none" stroke={s} strokeWidth="1" strokeDasharray="1 1"/>
      <line x1="8" y1="9" x2="8" y2="15" stroke={s} strokeWidth="1" strokeDasharray="1 1"/>
    </svg>
  );
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
      <polygon points="3,6 8,3 13,6 13,12 8,15 3,12" fill="none" stroke={s} strokeWidth="1"/>
      <polygon points="3,12 8,15 13,12 8,9" fill={f} stroke={s} strokeWidth="1"/>
    </svg>
  );
}

// ── Room Detail Popover (from design: RoomDetail) ──────────

function RoomDetail({
  room,
  onClose,
  hoverWallIdx,
  onHoverWall,
}: {
  room: RoomShape;
  onClose: () => void;
  hoverWallIdx: number | null;
  onHoverWall: (idx: number | null) => void;
}) {
  const floorLabel = room.floorId || '—';

  // Wände aus worldPts berechnen
  const walls = useMemo(() => {
    const pts = room.worldPts;
    const n = pts.length;
    const result: { idx: number; length: number; area: number }[] = [];
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const dx = pts[j].x - pts[i].x;
      const dy = pts[j].y - pts[i].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      result.push({ idx: i, length: len, area: len * room.height });
    }
    return result;
  }, [room]);

  const totalWallArea = walls.reduce((s, w) => s + w.area, 0);

  return (
    <div className="rb-scan__detail">
      <div className="rb-scan__detail-header">
        <div>
          <div className="rb-scan__detail-overline">Raum</div>
          <div className="rb-scan__detail-title">
            <span className="rb-scan__detail-code">{room.label}</span>
            <span>{room.name || room.usage || '—'}</span>
          </div>
        </div>
        <button className="rb-scan__detail-close" onClick={onClose}>×</button>
      </div>
      <div className="rb-scan__detail-grid">
        <div className="rb-scan__detail-label">Fläche</div>
        <div className="rb-scan__detail-value">{room.area.toFixed(1)} m²</div>
        <div className="rb-scan__detail-label">Höhe</div>
        <div className="rb-scan__detail-value">{room.height.toFixed(2)} m</div>
        <div className="rb-scan__detail-label">Geschoss</div>
        <div className="rb-scan__detail-value">{floorLabel}</div>
        <div className="rb-scan__detail-label">Nutzung</div>
        <div className="rb-scan__detail-value">{room.usage || '—'}</div>
        <div className="rb-scan__detail-label">Status</div>
        <div className="rb-scan__detail-value" style={{ textTransform: 'capitalize' }}>{room.status}</div>
      </div>

      {/* Wandflächen */}
      <div className="rb-scan__detail-walls-header">
        <span>Wandflächen</span>
        <span className="rb-scan__detail-walls-total">{totalWallArea.toFixed(1)} m²</span>
      </div>
      <div className="rb-scan__multi-rooms" onMouseLeave={() => onHoverWall(null)}>
        {walls.map((w) => (
          <div
            key={w.idx}
            className={`rb-scan__multi-room${hoverWallIdx === w.idx ? ' rb-scan__multi-room--hover' : ''}`}
            onMouseEnter={() => onHoverWall(w.idx)}
          >
            <span className="rb-scan__multi-room-code">W{w.idx + 1}</span>
            <span className="rb-scan__multi-room-name">{w.length.toFixed(2)} m × {room.height.toFixed(2)} m</span>
            <span className="rb-scan__multi-room-area">{w.area.toFixed(1)} m²</span>
          </div>
        ))}
      </div>

      <div className="rb-scan__detail-actions">
        <button className="rb-scan__detail-action">Dokumente</button>
        <div className="rb-scan__detail-divider" />
        <button className="rb-scan__detail-action">→ Grundriss</button>
      </div>
    </div>
  );
}

// ── Multi-Room Detail Popover (aggregated info) ──────────

function MultiRoomDetail({
  rooms,
  onClose,
  onHoverRoom,
  hoverRoomId,
}: {
  rooms: RoomShape[];
  onClose: () => void;
  onHoverRoom: (id: number | null) => void;
  hoverRoomId: number | null;
}) {
  const totalArea = rooms.reduce((s, r) => s + r.area, 0);
  const totalVolume = rooms.reduce((s, r) => s + r.area * r.height, 0);

  // Geschosse sammeln
  const floors = [...new Set(rooms.map(r => r.floorId || '—'))].sort(
    (a, b) => (FLOOR_ORDER[a] ?? 99) - (FLOOR_ORDER[b] ?? 99)
  );

  // Nutzungen sammeln mit Häufigkeit
  const usageMap = new Map<string, number>();
  rooms.forEach(r => {
    const u = r.usage || '—';
    usageMap.set(u, (usageMap.get(u) || 0) + 1);
  });
  const usages = [...usageMap.entries()].sort((a, b) => b[1] - a[1]);

  // Höhen-Range
  const heights = rooms.map(r => r.height);
  const minH = Math.min(...heights);
  const maxH = Math.max(...heights);
  const heightStr = minH === maxH
    ? `${minH.toFixed(2)} m`
    : `${minH.toFixed(2)}–${maxH.toFixed(2)} m`;

  return (
    <div className="rb-scan__detail rb-scan__detail--multi">
      <div className="rb-scan__detail-header">
        <div>
          <div className="rb-scan__detail-overline">Auswahl</div>
          <div className="rb-scan__detail-title">
            <span className="rb-scan__detail-code">{rooms.length}</span>
            <span>Räume</span>
          </div>
        </div>
        <button className="rb-scan__detail-close" onClick={onClose}>×</button>
      </div>

      {/* Kennzahlen als kompakte Kacheln */}
      <div className="rb-scan__multi-metrics">
        <div className="rb-scan__multi-metric">
          <div className="rb-scan__multi-metric-val">{totalArea.toFixed(1)}<span className="rb-scan__multi-metric-unit"> m²</span></div>
          <div className="rb-scan__multi-metric-lbl">Gesamtfläche</div>
        </div>
        <div className="rb-scan__multi-metric">
          <div className="rb-scan__multi-metric-val">{totalVolume.toFixed(1)}<span className="rb-scan__multi-metric-unit"> m³</span></div>
          <div className="rb-scan__multi-metric-lbl">Volumen</div>
        </div>
        <div className="rb-scan__multi-metric">
          <div className="rb-scan__multi-metric-val">{heightStr}</div>
          <div className="rb-scan__multi-metric-lbl">Höhen</div>
        </div>
        <div className="rb-scan__multi-metric">
          <div className="rb-scan__multi-metric-val">{floors.join(', ')}</div>
          <div className="rb-scan__multi-metric-lbl">Geschosse</div>
        </div>
      </div>

      {/* Nutzungen als Tags */}
      {usages.length > 0 && (
        <div className="rb-scan__multi-tags">
          {usages.map(([u, count]) => (
            <span key={u} className="rb-scan__multi-tag">
              {u}{count > 1 && <span className="rb-scan__multi-tag-count">×{count}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Raumliste */}
      <div className="rb-scan__multi-rooms" onMouseLeave={() => onHoverRoom(null)}>
        {rooms.map(r => (
          <div
            key={r.id}
            className={`rb-scan__multi-room${hoverRoomId === r.id ? ' rb-scan__multi-room--hover' : ''}`}
            onMouseEnter={() => onHoverRoom(r.id)}
          >
            <span className="rb-scan__multi-room-code">{r.label}</span>
            <span className="rb-scan__multi-room-name">{r.name || r.usage || '—'}</span>
            <span className="rb-scan__multi-room-area">{r.area.toFixed(1)} m²</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────

export default function ScanPanel({
  selectedVolumeIds,
  hoverVolumeId,
  onToggleVolume,
  onDeselectAll,
  onHoverVolume,
  compact,
}: ScanPanelProps) {
  const [rooms, setRooms] = useState<RoomShape[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [ctm, setCtm] = useState<DOMMatrix | null>(null);

  // Controls state
  const [explode, setExplode] = useState(12);
  const [volumeStyle, setVolumeStyle] = useState<VolumeStyle>('footprint');
  const [activeFloor, setActiveFloor] = useState<number | null>(null);
  const [isolateFloor, setIsolateFloor] = useState(false);

  // Hovered wall index (roomId:wallIdx) for highlighting side faces
  const [hoverWall, setHoverWall] = useState<{ roomId: number; wallIdx: number } | null>(null);

  const refresh = useCallback(() => setRooms(readRoomShapes()), []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2000);
    const h = () => refresh();
    window.addEventListener('orto:roomsChanged', h);
    return () => { clearInterval(t); window.removeEventListener('orto:roomsChanged', h); };
  }, [refresh]);

  // ── Selected rooms for detail popover ──
  const selectedRooms = useMemo(() => {
    if (selectedVolumeIds.size === 0) return [];
    return rooms.filter(r => selectedVolumeIds.has(r.id));
  }, [rooms, selectedVolumeIds]);

  const selectedRoom = useMemo(() => {
    if (selectedRooms.length !== 1) return null;
    return selectedRooms[0];
  }, [selectedRooms]);

  // ── Floor list (sorted bottom→top) ──
  const floorList = useMemo(() => {
    const map = new Map<string, { id: string; level: number; name: string }>();
    rooms.forEach(r => {
      if (r.floorId && !map.has(r.floorId)) {
        const level = FLOOR_ORDER[r.floorId] ?? 99;
        map.set(r.floorId, { id: r.floorId, level, name: r.floorId });
      }
    });
    return [...map.values()].sort((a, b) => a.level - b.level);
  }, [rooms]);

  // ── Iso Geometry ──
  const { isoRooms, viewBox, labelSize, gridLines } = useMemo(() => {
    if (rooms.length === 0) return { isoRooms: [], viewBox: '0 0 400 300', labelSize: 1, gridLines: [] };

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    // Track world bounds for grid
    let wMinX = Infinity, wMaxX = -Infinity, wMinY = Infinity, wMaxY = -Infinity;

    const isoRooms = rooms.map(room => {
      const floorLevel = FLOOR_ORDER[room.floorId || ''] ?? 0;
      const explodeZ = floorLevel * explode;
      const baseZ = room.worldPts[0]?.z ?? 0;

      const top = room.worldPts.map(p => toIso(p.x, p.y, baseZ + room.height + explodeZ));
      const bot = room.worldPts.map(p => toIso(p.x, p.y, baseZ + explodeZ));

      for (const p of [...top, ...bot]) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      room.worldPts.forEach(p => {
        if (p.x < wMinX) wMinX = p.x;
        if (p.x > wMaxX) wMaxX = p.x;
        if (p.y < wMinY) wMinY = p.y;
        if (p.y > wMaxY) wMaxY = p.y;
      });

      const cx = top.reduce((s, p) => s + p.x, 0) / top.length;
      const cy = top.reduce((s, p) => s + p.y, 0) / top.length;

      // Determine polygon winding in world-space XY
      const cw = isClockwise(room.worldPts);

      // Side faces — front-facing only (backface culling)
      const sides: { path: string; isRight: boolean; wallIdx: number; cx: number; cy: number }[] = [];
      // Back-facing walls (with path for lighter fill + label positioning)
      const backWalls: { path: string; isRight: boolean; wallIdx: number; cx: number; cy: number }[] = [];
      // Back edges — NOT front-facing, for dashed wireframe/glass rendering
      const backEdges: { x1: number; y1: number; x2: number; y2: number }[] = [];
      for (let i = 0; i < top.length; i++) {
        const j = (i + 1) % top.length;
        const facePath = pts2path([top[i], top[j], bot[j], bot[i]]);
        const faceCx = (top[i].x + top[j].x + bot[j].x + bot[i].x) / 4;
        const faceCy = (top[i].y + top[j].y + bot[j].y + bot[i].y) / 4;
        const isRight = Math.abs(top[j].x - top[i].x) < Math.abs(top[j].y - top[i].y);
        if (isFrontFace(top[i], top[j], cw)) {
          sides.push({ path: facePath, isRight, wallIdx: i, cx: faceCx, cy: faceCy });
        } else {
          backWalls.push({ path: facePath, isRight, wallIdx: i, cx: faceCx, cy: faceCy });
          // Hidden back edge — top segment
          backEdges.push({ x1: top[i].x, y1: top[i].y, x2: top[j].x, y2: top[j].y });
          // Hidden back edge — bottom segment
          backEdges.push({ x1: bot[i].x, y1: bot[i].y, x2: bot[j].x, y2: bot[j].y });
          // Hidden back edge — vertical
          backEdges.push({ x1: top[i].x, y1: top[i].y, x2: bot[i].x, y2: bot[i].y });
        }
      }

      // Vertical corner edges (every top[i] → bot[i])
      const verticals: { x1: number; y1: number; x2: number; y2: number }[] = [];
      for (let i = 0; i < top.length; i++) {
        verticals.push({ x1: top[i].x, y1: top[i].y, x2: bot[i].x, y2: bot[i].y });
      }

      // Depth key for Painter's sort (from design: depthKey = -(x+y) - z*2).
      // Objects with smaller depthKey are closer to camera → draw LAST.
      // We sort ascending, so use positive (x+y+2z) = further objects first.
      const avgX = room.worldPts.reduce((s, p) => s + p.x, 0) / room.worldPts.length;
      const avgY = room.worldPts.reduce((s, p) => s + p.y, 0) / room.worldPts.length;
      const midZ = baseZ + explodeZ + room.height / 2;

      return {
        ...room,
        floorLevel,
        topPath: pts2path(top),
        topPts: top,
        botPath: pts2path(bot),
        botPts: bot,
        sides,
        backWalls,
        backEdges,
        verticals,
        center: { x: cx, y: cy },
        explodeZ,
        sortZ: (avgX + avgY) + midZ * 2,
      };
    });

    const pad = Math.max((maxX - minX), (maxY - minY)) * 0.15;
    const w = maxX - minX + pad * 2;
    const h = maxY - minY + pad * 2;
    const labelSize = Math.max(w, h) * 0.022;

    // ── Isometric ground grid (z=0 plane) ──
    const gridStep = 5; // metres
    const gMinX = Math.floor(wMinX / gridStep) * gridStep - gridStep * 2;
    const gMaxX = Math.ceil(wMaxX / gridStep) * gridStep + gridStep * 2;
    const gMinY = Math.floor(wMinY / gridStep) * gridStep - gridStep * 2;
    const gMaxY = Math.ceil(wMaxY / gridStep) * gridStep + gridStep * 2;
    const gridLines: { x1: number; y1: number; x2: number; y2: number }[] = [];
    // X-parallel lines
    for (let gx = gMinX; gx <= gMaxX; gx += gridStep) {
      const a = toIso(gx, gMinY, 0);
      const b = toIso(gx, gMaxY, 0);
      gridLines.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    // Y-parallel lines
    for (let gy = gMinY; gy <= gMaxY; gy += gridStep) {
      const a = toIso(gMinX, gy, 0);
      const b = toIso(gMaxX, gy, 0);
      gridLines.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }

    return {
      isoRooms: isoRooms.sort((a, b) => a.sortZ - b.sortZ),
      viewBox: `${(minX - pad).toFixed(1)} ${(minY - pad).toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)}`,
      labelSize,
      gridLines,
    };
  }, [rooms, explode]);

  // ── SVG → pixel coordinate mapping (for HTML label overlays) ──
  const updateCtm = useCallback(() => {
    const svg = svgRef.current;
    const wrap = wrapRef.current;
    if (!svg || !wrap) return;
    const m = svg.getScreenCTM();
    if (!m) return;
    const wr = wrap.getBoundingClientRect();
    setCtm(new DOMMatrix([m.a, m.b, m.c, m.d, m.e - wr.left, m.f - wr.top]));
  }, []);

  useEffect(() => {
    // Re-compute when viewBox or layout changes
    requestAnimationFrame(updateCtm);
    const ro = new ResizeObserver(updateCtm);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('scroll', updateCtm, true);
    return () => { ro.disconnect(); window.removeEventListener('scroll', updateCtm, true); };
  }, [updateCtm, viewBox]);

  const svgToPx = useCallback((sx: number, sy: number) => {
    if (!ctm) return { x: 0, y: 0 };
    return { x: ctm.a * sx + ctm.c * sy + ctm.e, y: ctm.b * sx + ctm.d * sy + ctm.f };
  }, [ctm]);

  // ── Floor label positions (dark pills in SVG) ──
  // Place labels to the left of the scene, vertically centred on each floor
  const floorLabels = useMemo(() => {
    if (explode <= 0 || isoRooms.length === 0) return [];

    // Global leftmost projected x across all visible rooms
    let globalMinX = Infinity;
    isoRooms.forEach(r => {
      [...r.topPts, ...r.botPts].forEach(p => {
        if (p.x < globalMinX) globalMinX = p.x;
      });
    });

    return floorList.map(floor => {
      const floorRooms = isoRooms.filter(r => r.floorId === floor.id);
      if (floorRooms.length === 0) return null;

      // Vertical centre of this floor's projected rooms
      let minY = Infinity, maxY = -Infinity;
      floorRooms.forEach(r => {
        [...r.topPts, ...r.botPts].forEach(p => {
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        });
      });
      const cy = (minY + maxY) / 2;

      return { floor, pt: { x: globalMinX - labelSize * 1.5, y: cy } };
    }).filter(Boolean) as { floor: typeof floorList[0]; pt: { x: number; y: number } }[];
  }, [floorList, isoRooms, explode, labelSize]);

  // ── Ghosting / isolation logic ──
  const isGhost = useCallback((floorLevel: number) => {
    if (activeFloor == null) return false;
    return floorLevel !== activeFloor;
  }, [activeFloor]);

  const isHidden = useCallback((floorLevel: number) => {
    if (isolateFloor && activeFloor != null && floorLevel !== activeFloor) return true;
    return false;
  }, [isolateFloor, activeFloor]);

  // ── Colour resolution per room ──
  const getColors = useCallback((floorLevel: number) => {
    return FLOOR_PALETTE[floorLevel % FLOOR_PALETTE.length];
  }, []);

  // ── Empty State ──
  if (rooms.length === 0) {
    return (
      <div className={`rb-scan${compact ? ' rb-scan--compact' : ''}`}>
        <div className="rb-scan__empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.2">
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
          </svg>
          <span>Keine Räume vorhanden</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`rb-scan${compact ? ' rb-scan--compact' : ''}`}>
      {/* ── SVG Scene ── */}
      <svg
        ref={svgRef}
        className="rb-scan__svg"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => onHoverVolume(null)}
        onClick={(e) => { if (e.target === e.currentTarget) onDeselectAll(); }}
      >
        <defs>
          <filter id="iso-shadow" x="-10%" y="-10%" width="130%" height="140%">
            <feDropShadow dx="0" dy={labelSize * 0.3} stdDeviation={labelSize * 0.25} floodColor="#000" floodOpacity="0.05" />
          </filter>
        </defs>

        {/* Background */}
        <rect x="-99999" y="-99999" width="999999" height="999999" fill="#ffffff" />

        {/* Isometric ground grid */}
        {gridLines.map((l, i) => (
          <line
            key={`g${i}`}
            x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke="rgba(0,0,0,0.06)"
            strokeWidth={labelSize * 0.04}
          />
        ))}

        {/* Room volumes */}
        {isoRooms.map(room => {
          if (isHidden(room.floorLevel)) return null;

          const isSel = selectedVolumeIds.has(room.id);
          const isHov = hoverVolumeId === room.id;
          const ghost = isGhost(room.floorLevel);
          const colors = getColors(room.floorLevel);
          const opacity = ghost ? 0.12 : 1;

          // ── Resolve fills + stroke ──
          let topFill: string, rightFill: string, frontFill: string;
          let stroke: string, strokeW: number;
          let showBackEdges = false;

          // Ist ein Raum aus der Liste gehovert? → andere selektierte dimmen
          const listHoverActive = hoverVolumeId != null && selectedVolumeIds.has(hoverVolumeId);
          const isSelDimmed = isSel && !isHov && listHoverActive;

          if (isSel && isHov) {
            // Gehoverter selektierter Raum: heller/stärker
            topFill = 'rgba(96, 165, 250, 0.85)'; rightFill = 'rgba(59, 130, 246, 0.70)'; frontFill = 'rgba(37, 99, 235, 0.60)';
            stroke = '#1d4ed8';
            strokeW = labelSize * 0.12;
          } else if (isSelDimmed) {
            // Andere selektierte Räume dimmen
            topFill = mulAlpha(SEL.top, 0.45); rightFill = mulAlpha(SEL.right, 0.45); frontFill = mulAlpha(SEL.front, 0.45);
            stroke = mulAlpha(SEL.stroke, 0.40);
            strokeW = labelSize * 0.07;
          } else if (isSel) {
            topFill = SEL.top; rightFill = SEL.right; frontFill = SEL.front;
            stroke = SEL.stroke;
            strokeW = labelSize * 0.10;
          } else if (ghost) {
            topFill = GHOST.top; rightFill = GHOST.right; frontFill = GHOST.front;
            stroke = GHOST.stroke;
            strokeW = labelSize * 0.06;
          } else {
            topFill = colors.top; rightFill = colors.right; frontFill = colors.front;
            stroke = colors.stroke;
            strokeW = isHov ? labelSize * 0.08 : labelSize * 0.06;
          }

          // ── Volume style overrides ──
          if (!isSel && !ghost) {
            if (volumeStyle === 'wireframe') {
              topFill = 'none'; rightFill = 'none'; frontFill = 'none';
              strokeW = isHov ? labelSize * 0.08 : labelSize * 0.05;
              showBackEdges = !isHov;
            } else if (volumeStyle === 'glass') {
              topFill = mulAlpha(colors.top, 0.25);
              rightFill = mulAlpha(colors.right, 0.25);
              frontFill = mulAlpha(colors.front, 0.25);
              strokeW = isHov ? labelSize * 0.08 : labelSize * 0.06;
              showBackEdges = !isHov;
            } else if (volumeStyle === 'footprint') {
              topFill = 'none'; rightFill = 'none'; frontFill = 'none';
              stroke = isHov ? colors.stroke : mulAlpha(colors.stroke, 0.25);
              strokeW = isHov ? labelSize * 0.08 : labelSize * 0.04;
              showBackEdges = !isHov;
            }
          }
          // Ghost + wireframe/glass/footprint
          if (ghost && volumeStyle !== 'solid') {
            topFill = 'none'; rightFill = 'none'; frontFill = 'none';
            strokeW = labelSize * 0.04;
            showBackEdges = true;
          }

          return (
            <g
              key={room.id}
              opacity={opacity}
              style={{ cursor: ghost ? 'default' : 'pointer', transition: 'opacity 0.25s ease' }}
              filter={!ghost && !isSel && !isHov && volumeStyle === 'solid' ? 'url(#iso-shadow)' : undefined}
              onClick={() => !ghost && onToggleVolume(room.id)}
              onMouseEnter={() => !ghost && onHoverVolume(room.id)}
              onMouseLeave={() => onHoverVolume(null)}
            >
              {/* Hidden back edges (dashed, faint) — wireframe/glass/footprint */}
              {showBackEdges && room.backEdges.map((e, i) => (
                <line
                  key={`h${i}`}
                  x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
                  stroke={stroke}
                  strokeWidth={strokeW * 0.7}
                  strokeDasharray={`${labelSize * 0.15} ${labelSize * 0.22}`}
                  opacity={0.5}
                />
              ))}

              {/* Footprint: bottom polygon filled */}
              {volumeStyle === 'footprint' && !ghost && (
                <path
                  d={room.botPath}
                  fill={isSel ? SEL.top : isHov ? mulAlpha(colors.top, 0.35) : mulAlpha(colors.top, 0.12)}
                  stroke={isSel ? SEL.stroke : isHov ? colors.stroke : mulAlpha(colors.stroke, 0.25)}
                  strokeWidth={isSel ? labelSize * 0.10 : isHov ? labelSize * 0.08 : labelSize * 0.04}
                  strokeLinejoin="round"
                />
              )}

              {/* Back-facing side faces (lighter fill, drawn first) */}
              {room.backWalls.map((bw, i) => {
                const isWallHov = hoverWall != null && hoverWall.roomId === room.id && hoverWall.wallIdx === bw.wallIdx;
                const backFill = bw.isRight
                  ? mulAlpha(frontFill, 0.55)
                  : mulAlpha(rightFill, 0.55);
                return (
                  <path
                    key={`bw${i}`}
                    d={bw.path}
                    fill={isWallHov ? 'rgba(30, 64, 175, 0.85)' : backFill}
                    stroke={isWallHov ? 'rgba(30, 64, 175, 0.9)' : mulAlpha(stroke, 0.5)}
                    strokeWidth={isWallHov ? labelSize * 0.12 : strokeW * 0.7}
                    strokeLinejoin="round"
                    pointerEvents="all"
                    style={isWallHov ? { filter: 'drop-shadow(0 0 6px rgba(30, 64, 175, 0.5))' } : undefined}
                  />
                );
              })}

              {/* Front-facing side faces */}
              {room.sides.map((side, i) => {
                const isWallHov = hoverWall != null && hoverWall.roomId === room.id && hoverWall.wallIdx === side.wallIdx;
                return (
                  <path
                    key={i}
                    d={side.path}
                    fill={isWallHov ? 'rgba(30, 64, 175, 0.85)' : (side.isRight ? frontFill : rightFill)}
                    stroke={isWallHov ? 'rgba(30, 64, 175, 0.9)' : stroke}
                    strokeWidth={isWallHov ? labelSize * 0.12 : strokeW}
                    strokeLinejoin="round"
                    pointerEvents="all"
                    style={isWallHov ? { filter: 'drop-shadow(0 0 6px rgba(30, 64, 175, 0.5))' } : undefined}
                  />
                );
              })}

              {/* Vertical corner edges */}
              {room.verticals.map((v, i) => (
                <line
                  key={`v${i}`}
                  x1={v.x1} y1={v.y1} x2={v.x2} y2={v.y2}
                  stroke={stroke}
                  strokeWidth={strokeW}
                />
              ))}

              {/* Top face */}
              <path
                d={room.topPath}
                fill={topFill}
                stroke={stroke}
                strokeWidth={strokeW}
                strokeLinejoin="round"
                pointerEvents="all"
              />
            </g>
          );
        })}

      </svg>

      {/* ── HTML Label Overlays (fixed pixel sizes, don't scale with SVG) ── */}

      {/* Floor level pills */}
      {ctm && floorLabels.map(({ floor, pt }) => {
        const px = svgToPx(pt.x, pt.y);
        return (
          <div
            key={`fl-${floor.id}`}
            className="rb-scan__floor-pill"
            style={{ left: px.x, top: px.y }}
          >
            {floor.name}
          </div>
        );
      })}

      {/* Hover / selected room labels */}
      {ctm && isoRooms.map(room => {
        if (isHidden(room.floorLevel)) return null;
        const isSel = selectedVolumeIds.has(room.id);
        const isHov = hoverVolumeId === room.id;
        if (!isSel && !isHov) return null;
        // Bei Einzelselektion: Raum-Pill unterdrücken (Surface-Labels + Detail-Popover zeigen alles)
        if (isSel && selectedVolumeIds.size === 1) return null;

        const px = svgToPx(room.center.x, room.center.y);
        const code = room.label;
        const sub = `${room.name || room.usage} · ${room.area.toFixed(0)} m²`;

        return (
          <div
            key={`lbl-${room.id}`}
            className={`rb-scan__room-pill${isSel ? ' rb-scan__room-pill--sel' : ''}`}
            style={{ left: px.x, top: px.y }}
          >
            <div className="rb-scan__room-pill-leader" />
            <div className="rb-scan__room-pill-body">
              <span className="rb-scan__room-pill-code">{code}</span>
              <span className="rb-scan__room-pill-sub">{sub}</span>
            </div>
          </div>
        );
      })}

      {/* ── Surface Annotation Labels with Leader Lines ── */}
      {ctm && selectedRooms.length === 1 && (() => {
        const room = isoRooms.find(r => r.id === selectedRooms[0].id);
        if (!room || isHidden(room.floorLevel)) return null;
        const pts = selectedRooms[0].worldPts;
        const h = selectedRooms[0].height;
        const floorArea = selectedRooms[0].area;

        // Room center in pixel space (used to push labels outward)
        const centerPx = svgToPx(room.center.x, room.center.y);

        // Top face center (for area label)
        const topCx = room.topPts.reduce((s, p) => s + p.x, 0) / room.topPts.length;
        const topCy = room.topPts.reduce((s, p) => s + p.y, 0) / room.topPts.length;
        const topPx = svgToPx(topCx, topCy);

        // All walls — no front/back distinction for labels
        const allWalls = [...room.sides, ...room.backWalls];

        // Label dimensions (approximate for collision detection)
        const LABEL_W = 80;
        const LABEL_H = 18;
        const PUSH_BASE = 48;
        const PUSH_STEP = 22;

        // Compute initial outward positions for all walls
        const wallLabels = allWalls.map(wall => {
          const anchor = svgToPx(wall.cx, wall.cy);
          const dx = anchor.x - centerPx.x;
          const dy = anchor.y - centerPx.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const dirX = dx / dist;
          const dirY = dy / dist;
          const j = (wall.wallIdx + 1) % pts.length;
          const wdx = pts[j].x - pts[wall.wallIdx].x;
          const wdy = pts[j].y - pts[wall.wallIdx].y;
          const wallLen = Math.sqrt(wdx * wdx + wdy * wdy);
          return { wallIdx: wall.wallIdx, anchor, dirX, dirY, wallLen, push: PUSH_BASE, label: { x: 0, y: 0 } };
        });

        // Sort by angle so adjacent walls are resolved together
        wallLabels.sort((a, b) => Math.atan2(a.dirY, a.dirX) - Math.atan2(b.dirY, b.dirX));

        // Iterative overlap resolution (up to 8 passes)
        function labelsOverlap(a: { x: number; y: number }, b: { x: number; y: number }) {
          return Math.abs(a.x - b.x) < LABEL_W && Math.abs(a.y - b.y) < LABEL_H;
        }

        // Compute label positions
        for (const wl of wallLabels) {
          wl.label = { x: wl.anchor.x + wl.dirX * wl.push, y: wl.anchor.y + wl.dirY * wl.push };
        }

        for (let pass = 0; pass < 8; pass++) {
          let moved = false;
          for (let i = 0; i < wallLabels.length; i++) {
            for (let j = i + 1; j < wallLabels.length; j++) {
              if (labelsOverlap(wallLabels[i].label, wallLabels[j].label)) {
                // Push the one closer to center further out
                const distI = wallLabels[i].push;
                const distJ = wallLabels[j].push;
                const target = distI <= distJ ? wallLabels[j] : wallLabels[i];
                target.push += PUSH_STEP;
                target.label = { x: target.anchor.x + target.dirX * target.push, y: target.anchor.y + target.dirY * target.push };
                moved = true;
              }
            }
            // Also check against area label (centered on top face)
            if (labelsOverlap(wallLabels[i].label, topPx)) {
              wallLabels[i].push += PUSH_STEP;
              wallLabels[i].label = {
                x: wallLabels[i].anchor.x + wallLabels[i].dirX * wallLabels[i].push,
                y: wallLabels[i].anchor.y + wallLabels[i].dirY * wallLabels[i].push,
              };
              moved = true;
            }
          }
          if (!moved) break;
        }

        return (
          <>
            {/* Leader line SVG overlay */}
            <svg className="rb-scan__leader-svg">
              {wallLabels.map(w => {
                const isWallHov = hoverWall != null && hoverWall.roomId === room.id && hoverWall.wallIdx === w.wallIdx;
                return (
                  <g key={`lead-${w.wallIdx}`}>
                    <line
                      x1={w.anchor.x} y1={w.anchor.y}
                      x2={w.label.x} y2={w.label.y}
                      className={`rb-scan__leader-line${isWallHov ? ' rb-scan__leader-line--active' : ''}`}
                    />
                    <circle
                      cx={w.anchor.x} cy={w.anchor.y} r={2.5}
                      className={`rb-scan__leader-dot${isWallHov ? ' rb-scan__leader-dot--active' : ''}`}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Area label centered on top face */}
            <div
              className="rb-scan__surface-label rb-scan__surface-label--area"
              style={{ left: topPx.x, top: topPx.y }}
            >
              <span className="rb-scan__surface-label-area">{floorArea.toFixed(1)} m²</span>
            </div>

            {/* Wall labels — all identical style */}
            {wallLabels.map(w => {
              const wallArea = w.wallLen * h;
              const isWallHov = hoverWall != null && hoverWall.roomId === room.id && hoverWall.wallIdx === w.wallIdx;

              return (
                <div
                  key={`wl-${w.wallIdx}`}
                  className={`rb-scan__surface-label rb-scan__surface-label--wall${isWallHov ? ' rb-scan__surface-label--active' : ''}`}
                  style={{ left: w.label.x, top: w.label.y }}
                  onMouseEnter={() => setHoverWall({ roomId: room.id, wallIdx: w.wallIdx })}
                  onMouseLeave={() => setHoverWall(null)}
                >
                  <span className="rb-scan__surface-label-name">W{w.wallIdx + 1}</span>
                  <span className="rb-scan__surface-label-area">{wallArea.toFixed(1)} m²</span>
                </div>
              );
            })}
          </>
        );
      })()}

      {/* ── Room Detail Popover ── */}
      {selectedRoom && (
        <RoomDetail
          room={selectedRoom}
          onClose={() => onDeselectAll()}
          hoverWallIdx={hoverWall?.roomId === selectedRoom.id ? hoverWall.wallIdx : null}
          onHoverWall={(idx) => setHoverWall(idx != null ? { roomId: selectedRoom.id, wallIdx: idx } : null)}
        />
      )}
      {selectedRooms.length > 1 && (
        <MultiRoomDetail
          rooms={selectedRooms}
          onClose={() => onDeselectAll()}
          onHoverRoom={onHoverVolume}
          hoverRoomId={hoverVolumeId}
        />
      )}

      {/* ── Explode Slider (top-right overlay) ── */}
      <div className="rb-scan__explode">
        <span className="rb-scan__explode-label">EXPLODE</span>
        <input
          type="range"
          min={0} max={12} step={0.5}
          value={explode}
          onChange={e => setExplode(+e.target.value)}
          className="rb-scan__explode-slider"
        />
        <span className="rb-scan__explode-value">{explode.toFixed(1)}</span>
      </div>

      {/* ── Floor Dropdown (bottom-right, aligned with style bar) ── */}
      {floorList.length > 0 && (
        <div className="rb-scan__floor-drop">
          <span className="rb-scan__floor-drop-label">GESCHOSS</span>
          <div className="rb-scan__floor-drop-select-wrap">
            <select
              className="rb-scan__floor-drop-select"
              value={activeFloor ?? ''}
              onChange={e => {
                const v = e.target.value;
                setActiveFloor(v === '' ? null : Number(v));
                if (v === '') setIsolateFloor(false);
              }}
            >
              <option value="">Alle</option>
              {[...floorList].reverse().map(f => {
                const pal = FLOOR_PALETTE[f.level % FLOOR_PALETTE.length];
                return (
                  <option key={f.id} value={f.level}>
                    {f.name}
                  </option>
                );
              })}
            </select>
          </div>
          {activeFloor != null && (
            <label className="rb-scan__floor-drop-solo">
              <input
                type="checkbox"
                checked={isolateFloor}
                onChange={e => setIsolateFloor(e.target.checked)}
              />
              Solo
            </label>
          )}
        </div>
      )}

      {/* ── Volume Style Toggle (bottom-left overlay) ── */}
      <div className="rb-scan__style-bar">
        <span className="rb-scan__style-label">STIL</span>
        {(['solid', 'wireframe', 'glass', 'footprint'] as VolumeStyle[]).map(vs => {
          const active = volumeStyle === vs;
          const names: Record<VolumeStyle, string> = {
            solid: 'Solid', wireframe: 'Wireframe', glass: 'Glass', footprint: 'Footprint',
          };
          return (
            <button
              key={vs}
              className={`rb-scan__style-btn${active ? ' rb-scan__style-btn--active' : ''}`}
              onClick={() => setVolumeStyle(vs)}
            >
              <VolumeIcon kind={vs} active={active} />
              <span>{names[vs]}</span>
            </button>
          );
        })}
      </div>

      {/* ── Floor Legend (bottom) ── */}
      <div className="rb-scan__legend">
        {floorList.map(f => {
          const pal = FLOOR_PALETTE[f.level % FLOOR_PALETTE.length];
          return (
            <span key={f.id} className="rb-scan__legend-item">
              <span className="rb-scan__legend-dot" style={{ background: pal.stroke }} />
              {f.name}
            </span>
          );
        })}
      </div>
    </div>
  );
}
